import { Link } from "react-router-dom";
import { Check, Mail, MapPin, ArrowRight } from "lucide-react";
import { HomeHero } from "../components/home/HomeHero";
import { Marquee } from "../components/site/Marquee";
import { Reveal } from "../components/site/Reveal";
import { SectionHeading } from "../components/site/SectionHeading";
import { DirectModel } from "../components/site/DirectModel";
import { IndustryBento } from "../components/site/IndustryBento";
import { ProcessTabs } from "../components/site/ProcessTabs";
import { FaqList } from "../components/site/FaqList";
import { COMPANY, FAQ_HOME } from "../content/site";
import { useSeo } from "../lib/useSeo";

const EMPLOYER = [
  "Gezielte Suche nach Ihrem konkreten Anforderungsprofil",
  "Vorauswahl nach Qualifikation, Erfahrung, Sprache und Verfügbarkeit",
  "Koordination von Vorstellungsgesprächen",
  "Begleitung der Schritte rund um Unterlagen, Visum und Arbeitsaufnahme",
  "Schriftlich vereinbarte, transparente Konditionen",
];
const CANDIDATE = [
  "Kostenlose Registrierung in unserem Bewerberpool",
  "Persönliche Ansprechpartner in Deutschland",
  "Vorstellung bei Arbeitgebern, die zu Ihrem Profil passen",
  "Direkte Anstellung beim Unternehmen in Deutschland",
  "Verständliche Informationen zu Visum und Anerkennung",
];

const BenefitCard = ({ eyebrow, title, items, to, cta, testid, dark }) => (
  <Reveal className="h-full">
    <article className={`flex h-full flex-col rounded-[2rem] p-8 md:p-10 ${dark ? "bg-[color:var(--navy)] text-white" : "bg-[color:var(--mist)]"}`} data-testid={testid}>
      <p className={dark ? "text-xs font-bold uppercase tracking-[0.18em] text-[#8FD3CE]" : "eyebrow"}>{eyebrow}</p>
      <h3 className={`mt-4 text-2xl md:text-3xl font-bold tracking-tight ${dark ? "text-white" : "text-[color:var(--navy)]"}`}>{title}</h3>
      <ul className="mt-8 space-y-4 flex-1">
        {items.map((it) => (
          <li key={it} className="flex gap-3">
            <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full ${dark ? "bg-[color:var(--teal)]" : "bg-[color:var(--navy)]"}`}><Check size={14} className="text-white" /></span>
            <span className={dark ? "text-slate-200" : "text-slate-700"}>{it}</span>
          </li>
        ))}
      </ul>
      <Link to={to} className={`group mt-10 inline-flex items-center gap-2 font-semibold ${dark ? "text-[#8FD3CE]" : "text-[color:var(--navy)]"}`} data-testid={`${testid}-link`}>
        {cta} <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
      </Link>
    </article>
  </Reveal>
);

export default function Home() {
  useSeo("", "PACTORA Personalpartner aus Lübeck verbindet Unternehmen in ganz Deutschland mit Arbeitskräften aus dem Ausland – internationale Direktvermittlung mit persönlicher Begleitung.", "/");
  return (
    <>
      <HomeHero />
      <Marquee />

      <section className="bg-white py-24 md:py-36" data-testid="home-intro">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow">Über PACTORA</p></Reveal>
            <Reveal delay={0.08}>
              <p className="mt-6 text-2xl sm:text-3xl lg:text-[2.6rem] font-semibold leading-[1.25] tracking-tight text-[color:var(--navy)]">
                Wir bringen Menschen, die in Deutschland arbeiten möchten, mit Unternehmen zusammen, die <span className="font-serif-accent font-normal text-[color:var(--teal-ink)]">verlässlich</span> Personal suchen – persönlich, nachvollziehbar und mit dem Ziel einer dauerhaften Zusammenarbeit.
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-slate-600">
                Von unserem Sitz in Lübeck aus vermitteln wir in ganz Deutschland. Unser Team klärt den Personalbedarf von Arbeitgebern, wählt geeignete Bewerberinnen und Bewerber aus unserem Bewerberpool aus und begleitet die organisatorische Abstimmung bis zum vereinbarten Arbeitsbeginn.
              </p>
            </Reveal>
            <Reveal delay={0.2}><Link to="/ueber-uns" className="group mt-8 inline-flex items-center gap-2 font-semibold text-[color:var(--navy)]" data-testid="home-intro-about-link">Mehr über PACTORA <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" /></Link></Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-5">
            <figure className="relative h-full min-h-[340px] overflow-hidden rounded-[2rem] rounded-bl-[7rem]">
              <img src="/img/luebeck.webp" alt="Holstentor in Lübeck" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              <figcaption className="absolute bottom-4 right-4 rounded-full bg-white/90 px-4 py-2 text-xs font-semibold text-[color:var(--navy)] backdrop-blur">Sitz in Lübeck</figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      <section className="bg-white pb-24 md:pb-36" data-testid="home-benefits">
        <div className="container-x">
          <SectionHeading eyebrow="Ihre Vorteile" title="Zwei Zielgruppen. Ein klarer Weg." />
          <div className="mt-14 grid gap-5 lg:grid-cols-2">
            <BenefitCard testid="benefits-employer" eyebrow="Für Arbeitgeber" title="Personal gezielt international gewinnen" items={EMPLOYER} to="/unternehmen" cta="Leistungen für Unternehmen" dark />
            <BenefitCard testid="benefits-candidate" eyebrow="Für Bewerber" title="Ihr Weg zu einer Arbeitsstelle in Deutschland" items={CANDIDATE} to="/bewerber" cta="Informationen für Bewerber" />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[color:var(--navy-deep)] py-24 md:py-36 grain" data-testid="home-direct">
        <div className="container-x relative grid gap-14 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <SectionHeading light eyebrow="Direktvermittlung erklärt" title="Ihr Unternehmen stellt direkt ein. Wir vermitteln." />
            <Reveal delay={0.15}>
              <div className="mt-8 space-y-5 text-base md:text-lg leading-relaxed text-slate-300">
                <p>Bei der Direktvermittlung wird die Arbeitskraft unmittelbar bei Ihrem Unternehmen angestellt. Sie schließen den Arbeitsvertrag, zahlen das Gehalt und führen die Person wie jedes andere Teammitglied.</p>
                <p>PACTORA sucht, prüft und stellt geeignete Kandidaten vor und begleitet die Vermittlung bis zum Arbeitsbeginn. Dafür erhalten wir eine vorab individuell vereinbarte Vermittlungsprovision. Arbeitnehmerüberlassung oder Zeitarbeit bieten wir nicht an.</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-6"><DirectModel /></Reveal>
        </div>
      </section>

      <section className="bg-white py-24 md:py-36" data-testid="home-industries">
        <div className="container-x">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading eyebrow="Branchen und Berufe" title="Vom Fahrerhaus bis zur Station." intro="Wir vermitteln Arbeitskräfte in zahlreiche Berufsfelder. Die tatsächliche Vermittlung richtet sich nach Bewerberverfügbarkeit, Anforderungen und rechtlichen Voraussetzungen." />
            <Link to="/branchen" className="group inline-flex shrink-0 items-center gap-2 font-semibold text-[color:var(--navy)]" data-testid="home-industries-link">Alle Branchen <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" /></Link>
          </div>
          <div className="mt-14"><IndustryBento /></div>
        </div>
      </section>

      <section className="bg-[color:var(--mist)] py-24 md:py-36" data-testid="home-process">
        <div className="container-x">
          <SectionHeading eyebrow="Ablauf" title="Sieben Schritte – nachvollziehbar für beide Seiten." className="mb-12" />
          <ProcessTabs />
        </div>
      </section>

      <section className="bg-white py-24 md:py-36" data-testid="home-faq">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHeading eyebrow="Häufige Fragen" title="Gut zu wissen." /></div>
          <div className="lg:col-span-8"><FaqList items={FAQ_HOME} prefix="home-faq" /></div>
        </div>
      </section>

      <section className="bg-white pb-24 md:pb-36" data-testid="home-contact">
        <div className="container-x">
          <div className="grid gap-px overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-200 lg:grid-cols-3">
            <div className="bg-[color:var(--navy)] p-8 md:p-12 text-white">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8FD3CE]">Kontakt</p>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">Sprechen Sie uns an.</h2>
              <p className="mt-4 text-slate-300">Wir antworten in der Regel per E-Mail.</p>
            </div>
            <a href={`mailto:${COMPANY.email}`} className="group bg-white p-8 md:p-12 transition-colors hover:bg-[color:var(--mist)]" data-testid="home-contact-email">
              <Mail className="text-[color:var(--teal-ink)]" />
              <p className="mt-6 text-sm text-slate-500">E-Mail</p>
              <p className="mt-1 text-lg md:text-xl font-semibold text-[color:var(--navy)] break-all">{COMPANY.email}</p>
            </a>
            <a href={COMPANY.mapsUrl} target="_blank" rel="noopener noreferrer" className="group bg-white p-8 md:p-12 transition-colors hover:bg-[color:var(--mist)]" data-testid="home-contact-address">
              <MapPin className="text-[color:var(--teal-ink)]" />
              <p className="mt-6 text-sm text-slate-500">Anschrift</p>
              <p className="mt-1 text-lg md:text-xl font-semibold text-[color:var(--navy)]">{COMPANY.street}<br />{COMPANY.city}</p>
            </a>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link to="/personal-anfragen" className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--navy)] px-7 py-4 font-semibold text-white hover:bg-[color:var(--navy-deep)]" data-testid="home-contact-inquiry">Personal anfragen <ArrowRight size={17} /></Link>
            <Link to="/bewerber-registrierung" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[color:var(--teal)] px-7 py-[14px] font-semibold text-[color:var(--navy)] hover:bg-[color:var(--teal)] hover:text-white" data-testid="home-contact-register">Als Bewerber registrieren <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
